/**
 * API Service — calls Cloudflare Functions instead of Google Sheets directly.
 * All authenticated requests send the Google access token as a Bearer header.
 *
 * Configured via setApiContext(slug, token) — called by the auth and tenant stores
 * after login / session restore. No store imports here to avoid circular deps.
 */

let _slug  = ''
let _token = ''

/** Called by auth store and tenant store after they have both values. */
export function setApiContext (slug, token) {
  _slug  = slug  || _slug
  _token = token || _token
}

async function apiFetch (path, options = {}) {
  const res = await fetch(path, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(_token ? { Authorization: `Bearer ${_token}` } : {}),
      ...(options.headers || {})
    }
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
  return data
}

const base = () => `/api/${_slug}`

// ── Sheet reads ────────────────────────────────────────────────────────────
export const getRooms    = () => apiFetch(`${base()}/sheets/rooms`)
export const getPeriods  = () => apiFetch(`${base()}/sheets/periods`)
export const getUsers    = () => apiFetch(`${base()}/sheets/users`)
export const getBookings = () => apiFetch(`${base()}/sheets/bookings`)

// ── Booking CRUD ───────────────────────────────────────────────────────────
export const createBooking = (payload) =>
  apiFetch(`${base()}/sheets/bookings`, { method: 'POST', body: JSON.stringify(payload) })
export const updateBooking = (id, payload) =>
  apiFetch(`${base()}/sheets/bookings/${id}`, { method: 'PUT', body: JSON.stringify(payload) })
export const deleteBooking = (id) =>
  apiFetch(`${base()}/sheets/bookings/${id}`, { method: 'DELETE' })

// ── Room CRUD ──────────────────────────────────────────────────────────────
export const createRoom = (payload) =>
  apiFetch(`${base()}/sheets/rooms`, { method: 'POST', body: JSON.stringify(payload) })
export const updateRoom = (id, payload) =>
  apiFetch(`${base()}/sheets/rooms/${id}`, { method: 'PUT', body: JSON.stringify(payload) })
export const deleteRoom = (id) =>
  apiFetch(`${base()}/sheets/rooms/${id}`, { method: 'DELETE' })

// ── Period CRUD ────────────────────────────────────────────────────────────
export const createPeriod = (payload) =>
  apiFetch(`${base()}/sheets/periods`, { method: 'POST', body: JSON.stringify(payload) })
export const updatePeriod = (id, payload) =>
  apiFetch(`${base()}/sheets/periods/${id}`, { method: 'PUT', body: JSON.stringify(payload) })
export const deletePeriod = (id) =>
  apiFetch(`${base()}/sheets/periods/${id}`, { method: 'DELETE' })

// ── User CRUD ──────────────────────────────────────────────────────────────
export const createUser = (payload) =>
  apiFetch(`${base()}/sheets/users`, { method: 'POST', body: JSON.stringify(payload) })
export const updateUser = (id, payload) =>
  apiFetch(`${base()}/sheets/users/${id}`, { method: 'PUT', body: JSON.stringify(payload) })
export const deleteUser = (id) =>
  apiFetch(`${base()}/sheets/users/${id}`, { method: 'DELETE' })

// ── Auth login (called before context is set — slug passed explicitly) ─────
export const loginWithGoogle = ({ code, slug, redirectUri }) =>
  apiFetch('/api/auth/login', {
    method:  'POST',
    body:    JSON.stringify({ code, slug, redirectUri })
  })
